import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { Footer } from "../../components/footer/footer";
import { componentColors, screenBreackpoints } from "../../theme";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { SeoHelmet } from "../../components/seo-helmet/seo-helmet";
import { PageContainer } from "../../components/base-components/base-componets";
import { Mode, ALL_ID, ModeId, ModeAndTeam, TeamId } from "../../api/mode-stats/mode-stats.types";
import { getModeStats, modeUrlToMode } from "../../api/mode-stats/mode-stats";
import { MainContainer, MainContainerMb, MainTitle, ModeContainer, ModeContainerMb, TableContainer } from "./modes.styles";
import { BrawlersStatsTableCard } from "../../components/brawlers-stats-table-card/brawlers-stats-table-card";
import { ModesOptions } from "./modes-options/modes-options";
import { MODE_PARAM, TEAM_PARAM } from "./modes.constants";
import { formatNameToTitleCase } from "../../shared/functions";

export const Modes: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initMode: ModeId = searchParams.get(MODE_PARAM) ? searchParams.get(MODE_PARAM) as ModeId : ALL_ID;
  const initModeFormatted = initMode === ALL_ID ? ALL_ID : modeUrlToMode(initMode)
  const initTeam: TeamId = searchParams.get(TEAM_PARAM) ? searchParams.get(TEAM_PARAM) as TeamId : ALL_ID;
  const initTeamFormatted = initMode === ALL_ID ? ALL_ID : modeUrlToMode(initTeam)
  const [selectedMode, setSelectedMode] = useState<ModeAndTeam>({ mode: initModeFormatted, teamComposition: initTeamFormatted });
  const [currentModes, setCurrentModes] = useState<ModeAndTeam[]>([]);
  const [mode, setMode] = useState<Mode>();
  const isMobile = useMediaQuery(`(max-width:${screenBreackpoints.mobile})`);
  const { t } = useTranslation();

  const pageContainerRef = useRef<HTMLDivElement | null>(null);

  const handleNavigation = useCallback((mode: ModeAndTeam) => {
    setSelectedMode(mode);
    pageContainerRef.current!.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const loadModeStats = async () => {
      try {
        const modeStats = await getModeStats(selectedMode);
        if (selectedMode.mode !== ALL_ID && !modeStats) { handleNavigation({ mode: ALL_ID, teamComposition: ALL_ID }) }
        const sortModes = [...modeStats.currentModes].sort((a, b) => {
          const isA_5v5 = a.teamComposition === "TEAM_2T_5P";
          const isB_5v5 = b.teamComposition === "TEAM_2T_5P";
          if (isA_5v5 && !isB_5v5) return 1;
          if (!isA_5v5 && isB_5v5) return -1;
          return a.teamComposition.localeCompare(b.teamComposition);
        });
        //window.console.log(sortModes)
        setCurrentModes(sortModes);
        setMode(modeStats.mode);
      } catch (error) {
        window.console.log(error)
      }
    };
    loadModeStats()
  }, [selectedMode, handleNavigation]);

  const isAllModes = mode?.mode === null;
  const is5V5 = mode?.teamComposition === "TEAM_2T_5P"
  const modeLabelFormatted = mode?.mode?.replace("_5V5", "")
  const modeBaseName = mode
    ? t(`DOMINE.MODE.${modeLabelFormatted}`, {
      defaultValue: formatNameToTitleCase(mode.mode?.toLowerCase().replace("_", " ") ?? ""),
    })
    : "";
  const modeName = is5V5 ? `${modeBaseName} 5v5` : modeBaseName;
  const title = isAllModes
    ? t("PAGES.MODES.SEO.ALL.TITLE")
    : t("PAGES.MODES.SEO.MODE.TITLE", { modeName });
  const description = isAllModes
    ? t("PAGES.MODES.SEO.ALL.DESCRIPTION")
    : t("PAGES.MODES.SEO.MODE.DESCRIPTION", { modeName });
  const keywords = isAllModes
    ? t("PAGES.MODES.SEO.ALL.KEYWORDS")
    : t("PAGES.MODES.SEO.MODE.KEYWORDS", { modeName });

  const tableTitle = mode?.mode?.toString() === undefined
    ? t("PAGES.MODES.TABLE.BEST_BRAWLERS_ALL")
    : t("PAGES.MODES.TABLE.BEST_BRAWLERS_MODE", { mode: modeName });
  const tableHighlights = mode?.mode?.toString() === undefined
    ? [{ word: t("PAGES.MODES.ALL_MODES"), color: componentColors.linkText }]
    : [{ word: modeName, color: componentColors.linkText }];
  const shareElement = mode?.mode?.toString() === undefined
    ? t("PAGES.MODES.ALL_MODES")
    : modeName;
  const shareText = t("PAGES.MODES.TABLE.SHARE", { element: shareElement });

  return (
    <>
      <PageContainer isMobile={isMobile} ref={pageContainerRef}>
        {mode &&
          <Box>
            <SeoHelmet title={title} description={description} keywords={keywords} />
            <MainTitle component="h1" isMobile={isMobile}>
              {title}
            </MainTitle>
            <Typography marginBottom={"16px"}>
              {description}
            </Typography>
            {isMobile ?
              <MainContainerMb>
                {mode && (
                  <BrawlersStatsTableCard
                    brawlersStats={mode.brawlersStats}
                    lastUpdate={mode.lastUpdate}
                    title={tableTitle}
                    highlights={tableHighlights}
                    shareElement={shareElement}
                    shareText={shareText}
                    showTotalBattles={false}
                    nOptionVisible={0}
                  />
                )}
                {currentModes && (
                  <ModeContainerMb>
                    <ModesOptions
                      selectedMode={selectedMode}
                      modeOptions={currentModes}
                      onClickMode={(mode) => handleNavigation(mode)} />
                  </ModeContainerMb>
                )
                }
              </MainContainerMb>
              :
              <MainContainer>
                <TableContainer>
                  {mode && (
                    <BrawlersStatsTableCard
                      brawlersStats={mode.brawlersStats}
                      lastUpdate={mode.lastUpdate}
                      title={tableTitle}
                      highlights={tableHighlights}
                      shareElement={shareElement}
                      shareText={shareText}
                      showTotalBattles={false}
                      nOptionVisible={0}
                    />
                  )}
                </TableContainer>
                <ModeContainer>
                  {currentModes && (
                    <ModesOptions
                      selectedMode={selectedMode}
                      modeOptions={currentModes}
                      onClickMode={(mode) => handleNavigation(mode)} />
                  )}
                </ModeContainer>
              </MainContainer>
            }
          </Box>
        }
        <Footer />
      </PageContainer>
    </>
  );
}
